import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Popconfirm, Row, Select, Table } from "antd";

const { TextArea } = Input;
import { DeleteFilled, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { ISSUANCE } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";
import ModalComponent from "../../../components/formComponent/ModalComponent";
import { useCheckPosSetting } from "../../../customHooks/PosCustomHooks";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { AllowNegativeStockIssuance, AllowIssuanceWithoutDemand } from "../../../utils/PosSettingConstants";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: ISSUANCE,
  BranchId: null,
  BranchToId: null,
  DepartmentId: null,
  DepartmentToId: null,
  Gross: 0,
  Tax: 0,
  Discount: 0,
  Amount: 0,
  ParentTransactionMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
  BranchToId: null,
  DepartmentToId: null,

};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailRate, setAddDetailRate] = useState("");
  const [addDetailTax, setAddDetailTax] = useState("");
  const [addDetailDiscount, setAddDetailDiscount] = useState("");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(null);
  const [demandModalVisible, setDemandModalVisible] = useState(false);
  const [demandSearchText, setDemandSearchText] = useState("");
  const allowNegativeStockIssuance = useCheckPosSetting(AllowNegativeStockIssuance);
  const allowIssuanceWithoutDemand = useCheckPosSetting(AllowIssuanceWithoutDemand);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // Fetch demands when both BranchId and DepartmentId are available
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId) {
      fetchDemandsByBranchDepartment();
    } else {
      setSupportingTable((prev) => ({
        ...prev,
        Demands: [],
      }));
    }
  }, [formFields.BranchId, formFields.DepartmentId]);

  // In edit mode, fetch demands when both BranchId and DepartmentId are available
  useEffect(() => {
    if (isEditMode && formFields.BranchId && formFields.DepartmentId) {
      fetchDemandsByBranchDepartment();
    }
  }, [isEditMode, formFields.BranchId, formFields.DepartmentId]);

  // Fetch items when both Branch and Department are selected
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  const fetchTransactionRelatedData = async (id, dataType, options = {}) => {
    const { showErrorMessage = true, onClear, onSuccess } = options;

    if (!id) {
      if (onClear) onClear();
      return;
    }

    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      Id: id,
      DataType: dataType,
      BranchId: formFields.BranchId || null,
      DepartmentId: formFields.DepartmentId || null,
    };

    try {
      const response = await postRequest("/Inv_TransactionRelatedData", payload, controller);

      if (response.error) {
        const errorMsg = response.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      if (response.data.response === false) {
        const errorMsg = response.data.DataSet?.Table?.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      const dataSet = response.data;
      const data = dataSet.Data || dataSet.DataSet?.Table || [];

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      if (error.message !== "canceled") {
        console.error(`Error fetching ${dataType}:`, error);
        if (showErrorMessage) {
          message.error(`Failed to fetch ${dataType}`);
        }
      }
      if (onClear) onClear();
    }
  };

  const fetchDemandsByBranchDepartment = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      setSupportingTable((prev) => ({
        ...prev,
        Demands: [],
      }));
      return;
    }

    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      // BranchId: formFields.BranchId,
      // DepartmentId: formFields.DepartmentId,
      DataType: "GetPendingDemands",
    };

    try {
      const response = await postRequest("/Inv_TransactionRelatedData", payload, controller);

      if (response.error) {
        console.error("Error fetching demands:", response.errorMessage);
        setSupportingTable((prev) => ({
          ...prev,
          Demands: [],
        }));
        return;
      }

      if (response.data.response === false) {
        console.error("Error fetching demands:", response.data.DataSet?.Table?.errorMessage);
        setSupportingTable((prev) => ({
          ...prev,
          Demands: [],
        }));
        return;
      }

      const dataSet = response.data;
      const demands = dataSet.Data || dataSet.DataSet?.Table || [];

      setSupportingTable((prev) => ({
        ...prev,
        Demands: demands,
      }));
    } catch (error) {
      if (error.message !== "canceled") {
        console.error("Error fetching demands:", error);
      }
      setSupportingTable((prev) => ({
        ...prev,
        Demands: [],
      }));
    }
  };

  const fetchDemandItems = async (demandId) => {
    await fetchTransactionRelatedData(
      demandId,
      "GetDemandItems",
      {
        showErrorMessage: true,
        onClear: () => {
          setTransactionDetails([]);
        },
        onSuccess: (items) => {
          const mappedDetails = items.map((item) => {
            const remainingQty = (item.QtyIU ?? 0) - (item.IssuedQtyIU ?? 0);
            const demandRate = item.LastPurchaseRateIU ?? 0;

            return {
              ItemId: item.ItemId ?? item.Id,
              ItemName: item.ItemName ?? item.Name ?? "",
              PurchaseUnitId: item.PurchaseUnitId ?? item.UnitId ?? null,
              PurchaseUnit: item.PurchaseUnit ?? item.UnitName ?? "",
              QtyPU: 0,
              RatePU: 0,
              TaxPU: 0,
              DiscountPU: 0,
              IssuanceUnitId: item.IssuanceUnitId ?? item.UnitId ?? null,
              IssuanceUnit: item.IssuanceUnit ?? item.UnitName ?? "",
              QtyIU: remainingQty,
              RateIU: demandRate,
              RecipeUnitId: item.RecipeUnitId ?? null,
              QtyRU: item.QtyRU ?? 0,
              RateRU: item.RateRU ?? 0,
              IsEnable: item.IsEnable !== false,
              IssuedQtyIU: item.IssuedQtyIU ?? 0,
              DemandRemainingQtyIU: remainingQty,
              LastPurchaseRateIU: item.LastPurchaseRateIU ?? 0,
              StockIU: item.StockIU ?? 0,
              StockPU: item.StockPU ?? 0,
            };
          });

          setTransactionDetails(mappedDetails);
        },
      }
    );
  };

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: ISSUANCE,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };

  const fetchData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: ISSUANCE,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }
      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));
        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || dataSet.Data || [],
          Demands: supportingTable?.Demands || [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      if (data.name === "BranchId" || data.name === "DepartmentId") {
        updated.ParentTransactionMasterId = null;
        setTransactionDetails([]);
      }

      if (data.name === "ParentTransactionMasterId") {
        if (data.value) {
          if (!isEditMode) {
            fetchDemandItems(data.value);
          }
        } else {
          setTransactionDetails([]);
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];
  const demands = supportingTable?.Demands || [];

  const handleAddDetail = () => {

    const qty = clampQtyRateValue(addDetailQty);
    const rate = clampQtyRateValue(addDetailRate);
    const tax = parseFloat(addDetailTax) || 0;
    const discount = parseFloat(addDetailDiscount) || 0;

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }


      // Validate quantity against available stock in Issuance Unit (skip when AllowNegativeStockIssuance is enabled)
      if (!allowNegativeStockIssuance) {
        const stockIU = selectedItemForAdd.StockIU ?? 0;
        if (qty > stockIU) {
          message.error(`Quantity cannot exceed available stock. Available stock: ${stockIU.toFixed(2)} ${selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? ""}`);
          return;
        }
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: 0,
        RatePU: 0,
        TaxPU: 0,
        DiscountPU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: rate,
        TaxIU: tax,
        DiscountIU: discount,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: true,
        LastPurchaseRateIU: selectedItemForAdd.LastPurchaseRateIU ?? selectedItemForAdd.Rate ?? 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        StockPU: selectedItemForAdd.StockPU ?? 0,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailRate("");
      setAddDetailTax("");
      setAddDetailDiscount("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyIU" || field === "RateIU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];
    const currentDetail = updatedDetails[index];

    if (field === "QtyIU" && currentDetail?.DemandRemainingQtyIU !== undefined) {
      const qtyValue = value;
      const remainingQty = currentDetail?.DemandRemainingQtyIU ?? 0;

      if (remainingQty > 0 && qtyValue > remainingQty) {
        message.error(
          `Quantity cannot exceed Demand Remaining Quantity (${remainingQty}). Please enter a value less than or equal to ${remainingQty}.`
        );
        return;
      }
    }

    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (field === "QtyIU" || field === "RateIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
      updatedDetails[index].RateIU = updatedDetails[index].RateIU || 0;
    }
    if (field === "TaxIU" || field === "DiscountIU") {
      updatedDetails[index].TaxIU = updatedDetails[index].TaxIU ?? 0;
      updatedDetails[index].DiscountIU = updatedDetails[index].DiscountIU ?? 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleClearDemand = async (record) => {
    const transactionMasterId = record.Id ?? record.id ?? record.TransactionMasterId;
    if (!transactionMasterId) return;

    const payload = {
      OperationId: 5,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: transactionMasterId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);

      if (response.error) {
        message.error(response.errorMessage || "Error clearing demand");
        return;
      }

      if (response.data?.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error clearing demand"
        );
        return;
      }

      message.success(response.successMessage || "Demand cleared successfully");
      handleFormChange({ name: "ParentTransactionMasterId", value: null });
      setTransactionDetails([]);
      setDemandModalVisible(false);
      setDemandSearchText("");
      fetchDemandsByBranchDepartment();
    } catch (error) {
      if (error.message !== "canceled") {
        console.error("Error clearing demand:", error);
        message.error("Failed to clear demand");
      }
    }
  };

  const calculateTotals = () => {
    let gross = 0;
    let taxTotal = 0;
    let discountTotal = 0;
    transactionDetails.forEach((detail) => {
      const lineAmount = (detail.QtyIU || 0) * (detail.RateIU || 0);
      const tax = parseFloat(detail.TaxIU) || 0;
      const discount = parseFloat(detail.DiscountIU) || 0;
      gross += lineAmount;
      taxTotal += tax;
      discountTotal += discount;
    });
    const netAmount = gross + taxTotal - discountTotal;

    setFormFields((prev) => ({
      ...prev,
      Gross: gross,
      Tax: taxTotal,
      Discount: discountTotal,
      Amount: netAmount,
      TotalAmount: netAmount,
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [transactionDetails, formFields.Tax, formFields.Discount]);

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "Issuance")) {
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (!allowIssuanceWithoutDemand) {
      if (!formFields.ParentTransactionMasterId) {
        message.error("Please select a demand");
        return;
      }
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    const invalidItems = transactionDetails.filter(
      (detail) =>
        detail.DemandRemainingQtyIU !== undefined &&
        detail.DemandRemainingQtyIU > 0 &&
        (detail.QtyIU || 0) > (detail.DemandRemainingQtyIU || 0)
    );

    if (invalidItems.length > 0) {
      const invalidItemNames = invalidItems
        .map((item) => item.ItemName || "Unknown Item")
        .join(", ");
      message.error(
        `Quantity exceeds Demand Remaining Quantity for the following items: ${invalidItemNames}. Please adjust the quantities before saving.`
      );
      return;
    }

    // Validate stock quantity on Submit (skip when AllowNegativeStockIssuance is enabled)
    if (isSubmitFlag && !allowNegativeStockIssuance) {
      const stockInvalidItems = transactionDetails.filter((detail) =>
        detail.StockIU !== undefined &&
        parseFloat(detail.QtyIU) > parseFloat(detail.StockIU));

      if (stockInvalidItems.length > 0) {
        const invalidItemDetails = stockInvalidItems
          .map((item) => {
            return `${item.ItemName || "Unknown Item"} (Quantity: ${item.QtyIU.toFixed(2)}, Available Stock: ${item.StockIU.toFixed(2)} ${item.IssuanceUnit || ""})`;
          })
          .join("; ");
        message.error(
          `Quantity exceeds available stock for the following items: ${invalidItemDetails}. Please adjust the quantities before submitting.`
        );
        return;
      }
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: ISSUANCE,
      BranchId: formFields.BranchId,
      BranchToId: formFields.BranchToId,
      DepartmentId: formFields.DepartmentId,
      DepartmentToId: formFields.DepartmentToId,
      Gross: formFields.Gross || 0,
      Tax: formFields.Tax || 0,
      Discount: formFields.Discount || 0,
      Amount: formFields.Amount || 0,
      ParentTransactionMasterId: formFields.ParentTransactionMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: detail.QtyPU || 0,
        RatePU: detail.RatePU || 0,
        TaxPU: detail.TaxPU || 0,
        DiscountPU: detail.DiscountPU || 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: detail.RateIU || 0,
        TaxIU: detail.TaxIU || 0,
        DiscountIU: detail.DiscountIU || 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: detail.QtyRU || 0,
        RateRU: detail.RateRU || 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/issuance");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} issuance`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      { title: "Stock Quantity", dataIndex: "StockIU", key: "StockIU" },
      { title: "Last Purchase Rate", dataIndex: "LastPurchaseRateIU", key: "LastPurchaseRateIU" },
      ...(transactionDetails.some((d) => d.DemandRemainingQtyIU !== undefined)
        ? [
          {
            title: "Demand Remaining Qty",
            dataIndex: "DemandRemainingQtyIU",
            key: "DemandRemainingQtyIU",
            width: 150,
            align: "right",
            render: (value) => {
              const qty = parseFloat(value) || 0;
              return qty > 0 ? qty.toFixed(2) : "—";
            },
          },
        ]
        : []),
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.QtyIU}
            onChange={(e) =>
              handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
            max={record.DemandRemainingQtyIU || undefined}
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "RateIU",
        key: "RateIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.RateIU}
            onChange={(e) =>
              handleDetailChange(index, "RateIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      // {
      //   title: "Amount",
      //   key: "Amount",
      //   width: 120,
      //   align: "right",
      //   render: (_, record) => {
      //     const qty = record.QtyIU ?? 0;
      //     const rate = record.RateIU ?? 0;
      //     const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
      //     return amount.toFixed(2);
      //   },
      // },
      // {
      //   title: "Tax",
      //   dataIndex: "TaxIU",
      //   key: "TaxIU",
      //   width: 100,
      //   render: (_, record, index) => (
      //     <Input
      //       type="number"
      //       value={record.TaxIU ?? ""}
      //       onChange={(e) =>
      //         handleDetailChange(index, "TaxIU", parseFloat(e.target.value) || 0)
      //       }
      //       style={{ width: "100%" }}
      //       readOnly={formFields.IsSubmit}
      //     />
      //   ),
      // },
      // {
      //   title: "Discount",
      //   dataIndex: "DiscountIU",
      //   key: "DiscountIU",
      //   width: 100,
      //   render: (_, record, index) => (
      //     <Input
      //       type="number"
      //       value={record.DiscountIU ?? ""}
      //       onChange={(e) =>
      //         handleDetailChange(index, "DiscountIU", parseFloat(e.target.value) || 0)
      //       }
      //       style={{ width: "100%" }}
      //       readOnly={formFields.IsSubmit}
      //     />
      //   ),
      // },
      {
        title: "Net Amount",
        key: "NetAmount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = record.QtyIU ?? 0;
          const rate = record.RateIU ?? 0;
          const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
          const tax = parseFloat(record.TaxIU) || 0;
          const discount = parseFloat(record.DiscountIU) || 0;
          const netAmount = amount + tax - discount;
          return netAmount.toFixed(2);
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              />
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail, transactionDetails]
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
        readOnly={true}
      />

      <Col span={4}>
        <label style={{ display: "block", marginBottom: 5 }}>
          Transaction Date <span style={{ color: "red" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <>
            <DatePicker
              style={{ width: "100%" }}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </>
        ) : (
          <>
            <FormTextField
              name="TransactionDate"
              size={INPUT_SIZE}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
              readOnly={true}
            />
          </>
        )}
      </Col>

      <FormSelect
        colSpan={4}
        listItem={(supportingTable.Branches || []).filter(branch => (branch.IsBool == true || branch.IsBool == 1))}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={formFields.BranchId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={formFields.DepartmentId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={(supportingTable.Branches || []).filter(branch => branch.IsBool == false || branch.IsBool == 0)}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchToId"
        label="Branch To"
        value={formFields.BranchToId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit || !formFields.BranchId}
        placeholder={!formFields.BranchId ? "Select Branch first" : "Select branch to"}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentToId"
        label="Department To"
        value={formFields.DepartmentToId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <Col span={4}>
        <Row gutter={8} align="bottom">
          <Col span={20}>
            <FormSelect
              colSpan={24}
              listItem={demands}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="ParentTransactionMasterId"
              label="Demand"
              value={formFields.ParentTransactionMasterId}
              onChange={handleFormChange}
              required={allowIssuanceWithoutDemand ? false : true}
              disabled={formFields.IsSubmit || !formFields.BranchId || !formFields.DepartmentId}
              allowClear={false}
              placeholder={!formFields.BranchId || !formFields.DepartmentId ? "Select branch and department first" : "Select demand"}
            />
          </Col>
          <Col span={4}>
            <Button
              type="link"
              onClick={() => setDemandModalVisible(true)}
              disabled={formFields.IsSubmit || !formFields.BranchId || !formFields.DepartmentId}
              style={{ padding: "4px 0", marginBottom: 8 }}
            >
              Search
            </Button>
          </Col>
        </Row>
      </Col>

      <ModalComponent
        title="Select Demand"
        isModalVisible={demandModalVisible}
        handleCancel={() => {
          setDemandModalVisible(false);
          setDemandSearchText("");
        }}
        footer={null}
        width={700}
        closable={true}
      >
        <Input
          placeholder="Search by date, transaction no, branch, or department..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          value={demandSearchText}
          onChange={(e) => setDemandSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={
            (supportingTable?.Demands || []).filter((record) => {
              if (!demandSearchText.trim()) return true;
              const search = demandSearchText.toLowerCase().trim();
              const date = (record.Date ?? "").toString().toLowerCase();
              const name = (record.Name ?? "").toString().toLowerCase();
              const branch = (record.BranchName ?? "").toString().toLowerCase();
              const dept = (record.DepartmentName ?? "").toString().toLowerCase();
              return (
                date.includes(search) ||
                name.includes(search) ||
                branch.includes(search) ||
                dept.includes(search)
              );
            })
          }
          rowKey={(record) => record.Id ?? record.id ?? record.TransactionMasterId ?? Math.random()}
          pagination={{ pageSize: 10 }}
          size="small"
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest("button")) return;
              const demandId = record.Id ?? record.id ?? record.TransactionMasterId;
              if (demandId) {
                handleFormChange({ name: "ParentTransactionMasterId", value: demandId });
                if (!isEditMode) {
                  fetchDemandItems(demandId);
                }
                setDemandModalVisible(false);
                setDemandSearchText("");
              }
            },
            style: { cursor: "pointer" },
          })}
          columns={[
            { title: "Date", dataIndex: "Date", key: "Date", width: 120 },
            { title: "Transaction No", dataIndex: "Name", key: "Name", width: 150 },
            { title: "Branch", dataIndex: "BranchName", key: "BranchName" },
            { title: "Department", dataIndex: "DepartmentName", key: "DepartmentName" },
            {
              title: "Action",
              key: "action",
              width: 80,
              render: (_, record) => (
                <Popconfirm
                  title="Clear demand"
                  description="Are you sure you want to clear this demand?"
                  onConfirm={() => handleClearDemand(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    title="Clear"
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      </ModalComponent>

      <Col span={8}>
        <label style={{ display: "block", marginBottom: 5 }}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>

      {/* <FormTextField
        colSpan={4}
        label="Gross Amount"
        name="Gross"
        size={INPUT_SIZE}
        value={formFields.Gross}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Tax"
        name="Tax"
        size={INPUT_SIZE}
        value={formFields.Tax}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Discount"
        name="Discount"
        size={INPUT_SIZE}
        value={formFields.Discount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      /> */}
      <FormTextField
        colSpan={4}
        label="Net Amount"
        name="TotalAmount"
        size={INPUT_SIZE}
        value={formFields.TotalAmount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Total Items"
        name="TotalItems"
        size={INPUT_SIZE}
        value={transactionDetails.length}
        type="text"
        onChange={handleFormChange}
        readOnly={true}
      />
    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => (item?.ItemName ?? item?.Name ?? "") + " ( " + (item?.IssuanceUnit ?? item?.UnitName ?? "") + " ) ";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockIU = (item) => item?.StockIU ?? item?.Stock ?? 0;
  const getRateIU = (item) => item?.LastPurchaseRateIU ?? item?.Rate ?? 0;

  const labelStyle = {
    display: "block",
    marginBottom: 5,
    fontSize: 14,
    fontWeight: 500,
    color: "#262626",
  };

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add Item Detail
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                  if (item) {
                    const rate = getRateIU(item);
                    setAddDetailRate(rate > 0 ? rate.toString() : "");
                  } else {
                    setAddDetailRate("");
                  }
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                  if (item) {
                    const rate = getRateIU(item);
                    setAddDetailRate(rate > 0 ? rate.toString() : "");
                  } else {
                    setAddDetailRate("");
                  }
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                  if (item) {
                    const rate = getRateIU(item);
                    setAddDetailRate(rate > 0 ? rate.toString() : "");
                  } else {
                    setAddDetailRate("");
                  }
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Available stock</label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getStockIU(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => setAddDetailQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={1}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Rate</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailRate}
                onChange={(e) => setAddDetailRate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={1}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            {/* <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Tax</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailTax}
                onChange={(e) => setAddDetailTax(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={0.01}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Discount</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailDiscount}
                onChange={(e) => setAddDetailDiscount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={0.01}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col> */}
            <Col xs={24} sm={24} md={24} lg={24}>
              <Button
                type="primary"
                onClick={handleAddDetail}
                disabled={!selectedItemForAdd || !addDetailQty || !formFields.BranchId || !formFields.DepartmentId}
                style={{ marginTop: 8 }}
              >
                Add Item
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Issuance" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Issuance" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/issuance"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "Issuance", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/issuance")}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={false}
      addDetailEntryPanel={addDetailEntryPanel}
    />
  );
};

export default CreateEdit;
